import * as XLSX from 'xlsx';
import { CreditAnalysis } from '../types';
import { fmtCurrency, fmtPercent, fmtMil, fmtMilDiv } from './analysisFormatters';

type Formatter = (v: any, item?: any) => string;

const identity: Formatter = (v) => v ?? '';
const currency: Formatter = (v) => fmtCurrency(v);
const percent: Formatter = (v) => fmtPercent(v);
const mil: Formatter = (v) => fmtMil(v);
const milDiv: Formatter = (v) => fmtMilDiv(v);

const STATUS_LABELS: Record<string, string> = {
    PENDING: 'Pendente', APPROVED: 'Aprovado', REJECTED: 'Reprovado',
    PRE_APPROVED: 'Pré-aprovado', CANCELED: 'Cancelado',
};

const fmtStatus: Formatter = (v) => STATUS_LABELS[v?.toUpperCase()] || v || '-';

const fmtRecommendation: Formatter = (v) => {
    const r = (v || '').toUpperCase();
    if (r === 'APPROVED' || r === 'APROVADO') return 'Aprovado';
    if (r === 'REJECTED' || r === 'REPROVADO') return 'Reprovado';
    if (r === 'PENDING' || r === 'PENDENTE') return 'Pendente';
    return v || '-';
};

const fmtScore: Formatter = (v) => {
    if (!v) return '-';
    if (v === '0' || v === '0.0') return 'Não calculado';
    return v;
};

const fmtPdef: Formatter = (v, item?: any) => {
    if (item?.score && parseFloat(item.score) === 0) return 'Não calculado';
    if (!v) return '-';
    return `${parseFloat(v).toFixed(2)}%`;
};

const fmtPdefAgro: Formatter = (v, item?: any) => {
    if (item?.agroScore && parseFloat(item.agroScore) === 0) return 'Não calculado';
    if (!v) return '-';
    return `${parseFloat(v).toFixed(2)}%`;
};

const COLUMNS: { key: string; label: string; fmt: Formatter }[] = [
    { key: 'batchNumber', label: 'Lote', fmt: identity },
    { key: 'date', label: 'Data', fmt: identity },
    { key: 'assignor', label: 'Cedente', fmt: identity },
    { key: 'drawee', label: 'Sacado', fmt: identity },
    { key: 'document', label: 'CPF/CNPJ', fmt: identity },
    { key: 'type', label: 'Tipo', fmt: identity },
    { key: 'year', label: 'Ano', fmt: identity },
    { key: 'city', label: 'Cidade', fmt: identity },
    { key: 'state', label: 'UF', fmt: identity },
    { key: 'economicGroup', label: 'Grupo Econômico', fmt: identity },
    { key: 'qsa', label: 'QSA (R$ mil)', fmt: milDiv },
    { key: 'annualRevenue', label: 'Rec. Anual (R$ mil)', fmt: mil },
    { key: 'equity', label: 'PL (R$ mil)', fmt: mil },
    { key: 'rating', label: 'Rating', fmt: identity },
    { key: 'reasons', label: 'Não conformidades', fmt: (v) => Array.isArray(v) && v.length ? v.join('; ') : '-' },
    { key: 'recommendation', label: 'Recomendação', fmt: fmtRecommendation },
    { key: 'status', label: 'Decisão', fmt: fmtStatus },
    { key: 'suggestedValue', label: 'Valor Sugerido (R$)', fmt: currency },
    { key: 'approvedValue', label: 'Valor Aprovado (R$)', fmt: currency },
    { key: 'approvedPercent', label: '%Aprov/SCR', fmt: percent },
    { key: 'justification', label: 'Justificativa', fmt: identity },
    { key: 'originalValue', label: 'Vlr Original (R$)', fmt: currency },
    { key: 'dupPercent', label: '% Dup/SCR', fmt: percent },
    { key: 'creditObs', label: 'Obs Crédito', fmt: identity },
    { key: 'currentExposure', label: 'Exposição Atual', fmt: currency },
    { key: 'potentialExposure', label: 'Exposição Potencial', fmt: currency },
    { key: 'scrTotal', label: 'SCR Total (R$ mil)', fmt: milDiv },
    { key: 'scrRural', label: 'SCR Rural (R$ mil)', fmt: milDiv },
    { key: 'overdue', label: 'Vencido (R$ mil)', fmt: milDiv },
    { key: 'scrPrejuizo', label: 'Prejuízo (R$ mil)', fmt: milDiv },
    { key: 'score', label: 'Score Positivo', fmt: fmtScore },
    { key: 'pdef', label: 'Pdef Positivo', fmt: fmtPdef },
    { key: 'agroScore', label: 'AgroScore', fmt: fmtScore },
    { key: 'pdefAgro', label: 'Pdef Agro', fmt: fmtPdefAgro },
    { key: 'restrictions', label: 'Restrições', fmt: (v) => { if (!Array.isArray(v) || !v.length) return '-'; return v.map((r: any) => `${r.type}: ${r.count} (R$ ${r.balance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`).join('; '); } },
    { key: 'scoreQuod', label: 'Score Quod', fmt: identity },
    { key: 'scoreBoaVista', label: 'Score BoaVista', fmt: identity },
    { key: 'ccirHa', label: 'CCIR ha', fmt: (v) => { const n = parseFloat(v); return !isNaN(n) ? n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'; } },
    { key: 'ccirValue', label: 'CCIR R$ (mil)', fmt: mil },
    { key: 'patrimonioScr', label: 'Patrim/SCR %', fmt: (v) => v ? `${v}%` : '-' },
    { key: 'cprNumber', label: 'Nº do Contrato', fmt: identity },
];

export function exportAnalysisToExcel(data: CreditAnalysis[], filename?: string) {
    const rows = data.map(item =>
        COLUMNS.reduce((row, col) => {
            row[col.label] = col.fmt((item as any)[col.key], item);
            return row;
        }, {} as Record<string, string>)
    );

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Análise de Crédito');
    XLSX.writeFile(wb, filename || `analise-credito-${new Date().toISOString().slice(0, 10)}.xlsx`);
}
